port module Main exposing (..)

import Application
import Assessment
import Browser
import Config
import Html exposing (..)
import Html.Attributes
    exposing
        ( class
        , classList
        , disabled
        , href
        , id
        , name
        , required
        , src
        , value
        )
import Html.Events exposing (onClick, onInput)
import Http
import Json.Decode
import Maybe
import String


main =
    Browser.element
        { init = init
        , view = view
        , update = update
        , subscriptions = subscriptions
        }


type alias Model =
    { application : Application.Application
    , config : Config.Config
    , errors : Maybe (List String)
    , applicationErrors : List Error
    , applicationWasValidated : Bool
    , assessment : Assessment.Assessment
    , state : FormState
    }


type FormState
    = Loading
    | Editing
    | Submitting
    | Submitted


type alias Error =
    ( Application.Field, String )


init : () -> ( Model, Cmd Msg )
init _ =
    ( { application = Application.default
      , config = Config.default
      , errors = Nothing
      , applicationErrors = []
      , applicationWasValidated = False
      , assessment = Assessment.default
      , state = Loading
      }
    , Config.get GotConfig
    )


view : Model -> Html Msg
view model =
    div []
        [ nav
            [ class "navbar"
            , class "navbar-dark"
            , class "bg-primary"
            ]
            [ div [ class "container" ]
                [ a [ class "navbar-brand", href "/" ] [ img [ src "logo.png" ] [] ]
                ]
            ]
        , div
            [ class "p-5"
            , class "mb-4"
            , class "bg-light"
            ]
            [ div [ class "container" ]
                [ h1 [ class "display-4" ]
                    [ text "US Loan Eligibility Questionnaire" ]
                ]
            ]
        , div [ class "container" ]
            [ formSectionPersonalDetails model
            , formSectionProgrammeDetails model
            , formSectionAssistanceRequired model
            , if model.application.assistanceRequired == Application.Direct then
                formSectionFAFSADetails model

              else
                text ""
            , formSectionCOA model
            , if model.application.assistanceRequired == Application.Direct then
                formSectionEFA model

              else
                text ""
            , if model.application.assistanceRequired == Application.Direct then
                formSectionFinalCosts model

              else
                text ""
            , if model.application.assistanceRequired == Application.Direct then
                formSectionLoanEligibility model

              else
                text ""
            , if model.application.assistanceRequired == Application.Direct then
                formSectionHowMuchWouldYouLikeToBorrow model

              else
                text ""
            , div [ class "alert", class "alert-secondary" ]
                [ p []
                    [ text """ The exchange rate used for the completion of our
                    online form ( """
                    , text (String.fromFloat model.config.exchangeRate)
                    , text """) is used for calculation purposes only. The true
                    exchange rate is not known until the loans are processed
                    during the year. Using a static exchange rate for
                    calculations ensures a fair system for all students at
                    application stage."""
                    ]
                , p [] [ text """We have been advised by the US government that
                the origination fees stated above may change in the forthcoming
                year.""" ]
                , p []
                    [ text """Loans are administered by Student Finance. If you
                    have any problems please email """
                    , a [ Html.Attributes.href "mailto:usloans@herts.ac.uk" ]
                        [ text "usloans@herts.ac.uk" ]
                    , text "."
                    ]
                ]
            , case model.errors of
                Just errs ->
                    div [ class "alert", class "alert-danger" ]
                        (List.map (\err -> p [ class "mb-0" ] [ text err ]) errs)

                Nothing ->
                    text ""
            , if model.state == Submitted then
                div [ class "alert", class "alert-success" ]
                    [ text """Submitted, you may want to print a copy for
                    your records.""" ]

              else
                text ""
            , ul [ class "list-inline", class "float-end" ]
                [ li [ class "list-inline-item" ]
                    [ button
                        [ Html.Attributes.type_ "button"
                        , class "btn"
                        , onClick ResetApplication
                        , disabled (model.state /= Editing && model.state /= Submitted)
                        ]
                        [ text "Reset" ]
                    ]
                , li [ class "list-inline-item" ]
                    [ button
                        [ Html.Attributes.type_ "button"
                        , class "btn"
                        , class "btn-primary"
                        , class "btn-lg"
                        , onClick SubmitApplication
                        , disabled (model.state /= Editing)
                        ]
                        (if model.state == Submitting then
                            [ span
                                [ class "spinner-border"
                                , class "spinner-border-sm"
                                ]
                                []
                            , text "  Submitting"
                            ]

                         else
                            [ text "Submit" ]
                        )
                    ]
                ]
            ]
        ]


formSection : String -> List (Html msg) -> Html msg
formSection title children =
    div [ class "mb-4", class "d-block" ]
        (h2 [ class "mb-3" ] [ text title ] :: children)


formSectionPersonalDetails : Model -> Html Msg
formSectionPersonalDetails model =
    formSection "PersonalDetails"
        [ textField
            { attrs = []
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.FirstName
                    model.applicationErrors
            , field = Application.FirstName
            , label = "First Name"
            , name = "firstName"
            , showValidation = model.applicationWasValidated
            , val = model.application.firstName
            }
        , textField
            { attrs = []
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.Surname
                    model.applicationErrors
            , field = Application.Surname
            , label = "Surname"
            , name = "surname"
            , showValidation = model.applicationWasValidated
            , val = model.application.surname
            }
        , textField
            { attrs = []
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.StudentNumber
                    model.applicationErrors
            , field = Application.StudentNumber
            , label = "Student Number"
            , name = "studentNumber"
            , showValidation = model.applicationWasValidated
            , val = model.application.studentNumber
            }
        , textField
            { attrs = []
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.SocialSecurityNumber
                    model.applicationErrors
            , field = Application.SocialSecurityNumber
            , label = "Social Security Number"
            , name = "socialSecurityNumber"
            , showValidation = model.applicationWasValidated
            , val = model.application.socialSecurityNumber
            }
        , textField
            { attrs = [ Html.Attributes.type_ "date" ]
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.DateOfBirth
                    model.applicationErrors
            , field = Application.DateOfBirth
            , label = "Date of Birth"
            , name = "dateOfBirth"
            , showValidation = model.applicationWasValidated
            , val = model.application.dateOfBirth
            }
        , textField
            { attrs = []
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.EmailAddress
                    model.applicationErrors
            , field = Application.EmailAddress
            , label = "Email Address"
            , name = "emailAddress"
            , showValidation = model.applicationWasValidated
            , val = model.application.emailAddress
            }
        ]


textField :
    { attrs : List (Attribute Msg)
    , disabled : Bool
    , errors : List Error
    , field : Application.Field
    , label : String
    , name : String
    , showValidation : Bool
    , val : String
    }
    -> Html Msg
textField args =
    div [ class "form-group", class "mb-3" ]
        [ label [ class "mb-2", Html.Attributes.for args.name ]
            [ text args.label ]
        , input
            ([ class "form-control"
             , required True
             , id args.name
             , value args.val
             , onInput <| SetField args.field
             , validationClasses (List.isEmpty args.errors) args.showValidation
             , disabled args.disabled
             ]
                ++ args.attrs
            )
            []
        , formatErrors args.errors
        ]


formSectionProgrammeDetails : Model -> Html Msg
formSectionProgrammeDetails model =
    formSection "Programme Details"
        [ textField
            { attrs = []
            , disabled = model.state /= Editing
            , errors =
                errorsForField Application.Course
                    model.applicationErrors
            , field = Application.Course
            , label = "Programme of Study"
            , name = "course"
            , showValidation = model.applicationWasValidated
            , val = model.application.course
            }
        , div [ class "form-group", class "mb-3" ]
            [ label [ class "mb-2", Html.Attributes.for "yearOfStudy" ]
                [ text "Year of Study" ]
            , select
                [ class "form-select"
                , validationClasses
                    (List.isEmpty <|
                        errorsForField Application.YearOfStudy
                            model.applicationErrors
                    )
                    model.applicationWasValidated
                , required True
                , id "yearOfStudy"
                , onInput <| SetYearOfStudy
                , disabled (model.state /= Editing)
                ]
                (List.map
                    (\y ->
                        option
                            [ Html.Attributes.value y.id
                            , Html.Attributes.selected
                                (model.application.yearOfStudy.id == y.id)
                            ]
                            [ text y.description ]
                    )
                    model.config.yearsOfStudy
                )
            , formatErrors <|
                errorsForField Application.CostOfAttendance
                    model.applicationErrors
            ]
        , div [ class "form-group", class "mb-3" ]
            [ label [ class "mb-2", Html.Attributes.for "costofAttendance" ]
                [ text "Length of Study" ]
            , select
                [ class "form-select"
                , validationClasses
                    (List.isEmpty <|
                        errorsForField Application.CostOfAttendance
                            model.applicationErrors
                    )
                    model.applicationWasValidated
                , required True
                , id "costofAttendance"
                , onInput <| SetCostOfAttendance
                , disabled (model.state /= Editing)
                ]
                (List.map
                    (\c ->
                        option
                            [ Html.Attributes.value c.id
                            , Html.Attributes.selected
                                (model.application.costOfAttendance.id == c.id)
                            ]
                            [ text c.description ]
                    )
                    model.config.costsOfAttendance
                )
            , formatErrors <|
                errorsForField Application.CostOfAttendance
                    model.applicationErrors
            ]
        ]


formSectionAssistanceRequired : Model -> Html Msg
formSectionAssistanceRequired model =
    formSection "Assistance Required"
        [ div [ class "form-group", class "mb-3" ]
            [ select
                [ class "form-select"
                , validationClasses
                    (List.isEmpty <|
                        errorsForField Application.Assistance
                            model.applicationErrors
                    )
                    model.applicationWasValidated
                , required True
                , id "assistanceRequired"
                , onInput <| SetField Application.Assistance
                , disabled (model.state /= Editing)
                ]
                [ option [ value "DIRECT" ] [ text "Direct Loan" ]
                , option [ value "PRIVATE_ONLY" ] [ text "Private Loan Only" ]
                ]
            , small [ class "form-text", class "text-muted" ]
                [ text """
                Does your application include a Direct Loan or a Private Loan
                (e.g., Sallie Mae) only?
                """
                ]
            , formatErrors <|
                errorsForField Application.CostOfAttendance
                    model.applicationErrors
            ]
        ]


formSectionFAFSADetails : Model -> Html Msg
formSectionFAFSADetails model =
    formSection "FAFSA Details"
        [ fafsaQuestion
            { disabled = model.state /= Editing
            , field = Application.EmancipatedMinor
            , label = "Are you an emancipated minor?"
            , val = model.application.emancipatedMinor
            }
        , fafsaQuestion
            { disabled = model.state /= Editing
            , field = Application.Veteran
            , label = "Are you a veteran or a member of the armed forces?"
            , val = model.application.veteran
            }
        , fafsaQuestion
            { disabled = model.state /= Editing
            , field = Application.Orphan
            , label = "Are you a ward of court or an orphan?"
            , val = model.application.orphan
            }
        , fafsaQuestion
            { disabled = model.state /= Editing
            , field = Application.Homeless
            , label = "Are you a homeless or at risk of being homeless?"
            , val = model.application.homeless
            }
        , fafsaQuestion
            { disabled = model.state /= Editing
            , field = Application.Dependents
            , label = "Do you have legal dependents other than a spouse?"
            , val = model.application.dependents
            }
        , div [ class "form-group", class "mb-3" ]
            [ label [ class "mb-2", Html.Attributes.for "course" ]
                [ text "Your Status" ]
            , input
                [ class "form-control"
                , class "text-center"
                , disabled True
                , value
                    (case model.assessment.status of
                        Assessment.Dependent ->
                            "DEPENDENT"

                        Assessment.Independent ->
                            "INDEPENDENT"
                    )
                , required True
                , id "course"
                , onInput <| SetField Application.Course
                ]
                []
            ]
        , if model.assessment.status == Assessment.Dependent then
            editCurrencyDollar
                { label = "SAR 'EFC'"
                , field = Application.SAREFCDollar
                , poundVal = model.assessment.sarEFC.pound
                , dollarVal = model.application.sarEFCDollar
                , errors =
                    errorsForField Application.SAREFCDollar
                        model.applicationErrors
                , showValidation = model.applicationWasValidated
                , disabled = model.state /= Editing
                }

          else
            text ""
        ]


fafsaQuestion :
    { disabled : Bool
    , field : Application.Field
    , val : Bool
    , label : String
    }
    -> Html Msg
fafsaQuestion args =
    div []
        [ div [ class "row" ]
            [ label [ class "col-sm-8", class "col-form-label" ]
                [ text args.label ]
            , label
                [ class "text-right"
                , class "col-sm-4"
                , class "col-form-label"
                ]
                [ div [ class "form-check", class "form-check-inline" ]
                    [ label [ class "form-check-label" ]
                        [ input
                            [ Html.Attributes.type_ "radio"
                            , class "form-check-input"
                            , value "true"
                            , onInput <| SetField args.field
                            , Html.Attributes.checked args.val
                            , disabled args.disabled
                            ]
                            []
                        ]
                    , text " Yes"
                    ]
                , div [ class "form-check", class "form-check-inline" ]
                    [ label [ class "form-check-label" ]
                        [ input
                            [ Html.Attributes.type_ "radio"
                            , class "form-check-input"
                            , value "false"
                            , Html.Attributes.checked (not args.val)
                            , onInput <| SetField args.field
                            , disabled args.disabled
                            ]
                            []
                        ]
                    , text " No"
                    ]
                ]
            ]
        , hr [] []
        ]


editCurrencyDollar :
    { dollarVal : Float
    , errors : List Error
    , field : Application.Field
    , label : String
    , poundVal : Float
    , showValidation : Bool
    , disabled : Bool
    }
    -> Html Msg
editCurrencyDollar args =
    div
        [ class "form-group"
        ]
        [ div [ class "row" ]
            [ div [ class "col-md-12", class "col-lg-8" ]
                [ label [ class "col-form-label" ] [ text args.label ] ]
            , div [ class "col-md-12", class "col-lg-2" ]
                [ div [ class "input-group" ]
                    [ div [ class "input-group-prepend" ]
                        [ span [ class "input-group-text" ] [ text "£" ] ]
                    , input
                        [ class "form-control"
                        , Html.Attributes.type_ "number"
                        , disabled True
                        , Html.Attributes.min "0"
                        , value (String.fromFloat args.poundVal)
                        ]
                        []
                    ]
                ]
            , div [ class "col-md-12", class "col-lg-2" ]
                [ div [ class "input-group" ]
                    [ div [ class "input-group-prepend" ]
                        [ span [ class "input-group-text" ] [ text "$" ] ]
                    , input
                        [ class "form-control"
                        , Html.Attributes.type_ "number"
                        , Html.Attributes.min "0"
                        , value (String.fromFloat args.dollarVal)
                        , onInput <| SetField args.field
                        , validationClasses (List.isEmpty args.errors)
                            args.showValidation
                        , disabled args.disabled
                        ]
                        []
                    ]
                ]
            ]
        , formatErrors args.errors
        , hr [] []
        ]


editCurrencyPound :
    { dollarVal : Float
    , errors : List Error
    , field : Application.Field
    , label : String
    , poundVal : Float
    , showValidation : Bool
    , disabled : Bool
    }
    -> Html Msg
editCurrencyPound args =
    div
        [ class "form-group"
        ]
        [ div [ class "row" ]
            [ div [ class "col-md-12", class "col-lg-8" ]
                [ label [ class "col-form-label" ] [ text args.label ] ]
            , div [ class "col-md-12", class "col-lg-2" ]
                [ div [ class "input-group" ]
                    [ div [ class "input-group-prepend" ]
                        [ span [ class "input-group-text" ] [ text "£" ] ]
                    , input
                        [ class "form-control"
                        , Html.Attributes.type_ "number"
                        , Html.Attributes.min "0"
                        , value (String.fromFloat args.poundVal)
                        , onInput <| SetField args.field
                        , validationClasses (List.isEmpty args.errors)
                            args.showValidation
                        , disabled args.disabled
                        ]
                        []
                    ]
                ]
            , div [ class "col-md-12", class "col-lg-2" ]
                [ div [ class "input-group" ]
                    [ div [ class "input-group-prepend" ]
                        [ span [ class "input-group-text" ] [ text "$" ] ]
                    , input
                        [ class "form-control"
                        , Html.Attributes.type_ "number"
                        , Html.Attributes.min "0"
                        , disabled True
                        , value (String.fromFloat args.dollarVal)
                        ]
                        []
                    ]
                ]
            ]
        , formatErrors args.errors
        , hr [] []
        ]


viewCurrency : String -> Float -> Float -> Html Msg
viewCurrency labelText poundVal dollarVal =
    div
        [ class "form-group"
        ]
        [ div [ class "row" ]
            [ div [ class "col-md-12", class "col-lg-8" ]
                [ label [ class "col-form-label" ] [ text labelText ] ]
            , div [ class "col-md-12", class "col-lg-2" ]
                [ div [ class "input-group" ]
                    [ div [ class "input-group-prepend" ]
                        [ span [ class "input-group-text" ] [ text "£" ] ]
                    , input
                        [ class "form-control"
                        , Html.Attributes.type_ "number"
                        , Html.Attributes.min "0"
                        , value (String.fromFloat poundVal)
                        , disabled True
                        ]
                        []
                    ]
                ]
            , div [ class "col-md-12", class "col-lg-2" ]
                [ div [ class "input-group" ]
                    [ div [ class "input-group-prepend" ]
                        [ span [ class "input-group-text" ] [ text "$" ] ]
                    , input
                        [ class "form-control"
                        , Html.Attributes.type_ "number"
                        , Html.Attributes.min "0"
                        , disabled True
                        , value (String.fromFloat dollarVal)
                        ]
                        []
                    ]
                ]
            ]
        , hr [] []
        ]


formSectionCOA : Model -> Html Msg
formSectionCOA model =
    formSection "Institutional Cost of Attendance (CoA)"
        [ editCurrencyPound
            { label = "Tuition Fees"
            , poundVal = model.application.tuitionFeePound
            , dollarVal = model.assessment.coaTuitionFee.dollar
            , field = Application.TuitionFeePound
            , errors =
                errorsForField Application.TuitionFeePound
                    model.applicationErrors
            , showValidation = model.applicationWasValidated
            , disabled = model.state /= Editing
            }
        , viewCurrency "Living Costs"
            model.assessment.coaLivingCosts.pound
            model.assessment.coaLivingCosts.dollar
        , viewCurrency "Books and Supplies"
            model.assessment.coaBooks.pound
            model.assessment.coaBooks.dollar
        , viewCurrency "Transportation"
            model.assessment.coaTransport.pound
            model.assessment.coaTransport.dollar
        , viewCurrency "Total CoA"
            model.assessment.coaTotal.pound
            model.assessment.coaTotal.dollar
        ]


formSectionEFA : Model -> Html Msg
formSectionEFA model =
    formSection "Estimated Financial Assistance (EFA)"
        [ editCurrencyPound
            { label = """How much of your tuition fee will be paid to the
            University by a sponsor?"""
            , poundVal = model.application.efaSponsorPound
            , dollarVal = model.assessment.efaSponsor.dollar
            , field = Application.EFASponsorPound
            , errors =
                errorsForField Application.EFASponsorPound
                    model.applicationErrors
            , showValidation = model.applicationWasValidated
            , disabled = model.state /= Editing
            }
        , editCurrencyPound
            { label = """How much have you been awarded in scholarships by the
            University?"""
            , poundVal = model.application.efaScholarshipUniPound
            , dollarVal = model.assessment.efaScholarshipUni.dollar
            , field = Application.EFAScholarshipUniPound
            , errors =
                errorsForField Application.EFAScholarshipUniPound
                    model.applicationErrors
            , showValidation = model.applicationWasValidated
            , disabled = model.state /= Editing
            }
        , editCurrencyPound
            { label = """How much have you been awarded by other scholarships
            or financial aid in the UK?"""
            , poundVal = model.application.efaScholarshipUKOtherPound
            , dollarVal = model.assessment.efaScholarshipUKOther.dollar
            , field = Application.EFAScholarshipUKOtherPound
            , errors =
                errorsForField Application.EFAScholarshipUKOtherPound
                    model.applicationErrors
            , showValidation = model.applicationWasValidated
            , disabled = model.state /= Editing
            }
        , editCurrencyDollar
            { label = """How much have you been awarded by other scholarships
            or financial aid in the US (e.g., Sallie Mae)?"""
            , field = Application.EFAScholarshipUSOtherDollar
            , poundVal = model.assessment.efaScholarshipUSOther.pound
            , dollarVal = model.application.efaScholarshipUSOtherDollar
            , errors =
                errorsForField Application.EFAScholarshipUSOtherDollar
                    model.applicationErrors
            , showValidation = model.applicationWasValidated
            , disabled = model.state /= Editing
            }
        , if model.assessment.status == Assessment.Dependent then
            viewCurrency "EFA (from above)"
                model.assessment.sarEFC.pound
                model.assessment.sarEFC.dollar

          else
            text ""
        , viewCurrency "Total EFA"
            model.assessment.efaTotal.pound
            model.assessment.efaTotal.dollar
        ]


formSectionFinalCosts : Model -> Html Msg
formSectionFinalCosts model =
    formSection "Final Costs"
        [ viewCurrency "Total COA"
            model.assessment.coaTotal.pound
            model.assessment.coaTotal.dollar
        , viewCurrency "Total EFA"
            model.assessment.efaTotal.pound
            model.assessment.efaTotal.dollar
        , viewCurrency """Total cost of attendance for which US loan assistance
        is available"""
            model.assessment.assistanceFor.pound
            model.assessment.assistanceFor.dollar
        ]


formSectionLoanEligibility : Model -> Html Msg
formSectionLoanEligibility model =
    formSection "Loan eligibility"
        [ threeColumnAmount "Unsubsidised"
            []
            [ currencyValue "$"
                "availabel"
                model.assessment.unsubsidisedAvailable.dollar
            ]
            []
        , threeColumnAmount "Subsidised"
            []
            [ currencyValue "$"
                "availabel"
                model.assessment.subsidisedAvailable.dollar
            ]
            []
        , threeColumnAmount "PLUS Loan"
            []
            [ currencyValue "$"
                "availabel"
                model.assessment.plusLoanAvailable.dollar
            ]
            []
        , threeColumnAmount "Total"
            []
            [ currencyValue "$"
                "availabel"
                model.assessment.availableTotal.dollar
            ]
            []
        ]


threeColumnAmount :
    String
    -> List (Html msg)
    -> List (Html msg)
    -> List Error
    -> Html msg
threeColumnAmount labelText lColContent rColContent errors =
    div []
        [ div [ class "row" ]
            [ div [ class "col-md-12", class "col-lg-6" ]
                [ label [ class "col-form-label" ] [ text labelText ] ]
            , div [ class "col-md-12", class "col-lg-3" ] lColContent
            , div [ class "col-md-12", class "col-lg-3" ] rColContent
            ]
        , formatErrors errors
        , hr [] []
        ]


currencyValue : String -> String -> Float -> Html msg
currencyValue preText postText val =
    div [ class "input-group" ]
        [ div [ class "input-group-prepend" ]
            [ span [ class "input-group-text" ] [ text preText ]
            ]
        , input
            [ class "form-control"
            , Html.Attributes.type_ "number"
            , disabled True
            , value (String.fromFloat val)
            ]
            []
        , div [ class "input-group-append" ]
            [ span [ class "input-group-text" ] [ text postText ]
            ]
        ]


formSectionHowMuchWouldYouLikeToBorrow : Model -> Html Msg
formSectionHowMuchWouldYouLikeToBorrow model =
    formSection "How much would you like to borrow?"
        [ threeColumnAmount
            ("Direct Loan - Unsubsidised - ("
                ++ String.fromFloat model.config.originationFeeUnsubsidised
                ++ "% origination fee)"
            )
            [ editCurrencyValue
                { preText = "$"
                , postText = "requested"
                , field = Application.UnsubsidisedRequestDollar
                , val = model.application.unsubsidisedRequestDollar
                , max = model.assessment.unsubsidisedAvailable.dollar
                , errors =
                    errorsForField Application.UnsubsidisedRequestDollar
                        model.applicationErrors
                , showValidation = model.applicationWasValidated
                , disabled = model.state /= Editing
                }
            ]
            [ currencyValue "$"
                "after fee"
                model.assessment.unsubsidisedRequestMinusFee.dollar
            ]
            (errorsForField Application.UnsubsidisedRequestDollar
                model.applicationErrors
            )
        , threeColumnAmount
            ("Direct Loan - Subsidised - ("
                ++ String.fromFloat model.config.originationFeeSubsidised
                ++ "% origination fee)"
            )
            [ editCurrencyValue
                { preText = "$"
                , postText = "requested"
                , field = Application.SubsidisedRequestDollar
                , val = model.application.subsidisedRequestDollar
                , max = model.assessment.subsidisedAvailable.dollar
                , errors =
                    errorsForField Application.SubsidisedRequestDollar
                        model.applicationErrors
                , showValidation = model.applicationWasValidated
                , disabled = model.state /= Editing
                }
            ]
            [ currencyValue "$"
                "after fee"
                model.assessment.subsidisedRequestMinusFee.dollar
            ]
            (errorsForField Application.SubsidisedRequestDollar
                model.applicationErrors
            )
        , threeColumnAmount
            ("PLUS Loan - ("
                ++ String.fromFloat model.config.originationFeePlus
                ++ "% origination fee)"
            )
            [ editCurrencyValue
                { preText = "$"
                , postText = "requested"
                , field = Application.PlusRequestDollar
                , val = model.application.plusRequestDollar
                , max = model.assessment.plusLoanAvailable.dollar
                , errors =
                    errorsForField Application.PlusRequestDollar
                        model.applicationErrors
                , showValidation = model.applicationWasValidated
                , disabled = model.state /= Editing
                }
            ]
            [ currencyValue "$"
                "after fee"
                model.assessment.plusRequestMinusFee.dollar
            ]
            (errorsForField Application.PlusRequestDollar
                model.applicationErrors
            )
        , threeColumnAmount "Total Loan"
            [ currencyValue "$"
                "requested"
                model.assessment.requestTotal.dollar
            ]
            [ currencyValue "$"
                "after fee"
                model.assessment.requestMinusFeeTotal.dollar
            ]
            []
        ]


editCurrencyValue :
    { preText : String
    , postText : String
    , val : Float
    , field : Application.Field
    , max : Float
    , errors : List Error
    , showValidation : Bool
    , disabled : Bool
    }
    -> Html Msg
editCurrencyValue args =
    div [ class "input-group" ]
        [ div [ class "input-group-prepend" ]
            [ span [ class "input-group-text" ] [ text args.preText ]
            ]
        , input
            [ class "form-control"
            , Html.Attributes.type_ "number"
            , value (String.fromFloat args.val)
            , Html.Attributes.min "0"
            , Html.Attributes.max (String.fromFloat args.max)
            , onInput
                (\s ->
                    SetField args.field
                        (String.fromFloat <|
                            min args.max <|
                                Maybe.withDefault 0 <|
                                    String.toFloat s
                        )
                )
            , disabled (args.disabled || args.max == 0)
            , validationClasses (List.isEmpty args.errors) args.showValidation
            ]
            []
        , div [ class "input-group-append" ]
            [ span [ class "input-group-text" ] [ text args.postText ]
            ]
        ]


validationClasses : Bool -> Bool -> Attribute msg
validationClasses isValid showValidation =
    classList
        [ ( "is-valid"
          , isValid && showValidation
          )
        , ( "is-invalid"
          , not isValid && showValidation
          )
        ]


errorsForField : Application.Field -> List Error -> List Error
errorsForField field errors =
    errors
        |> List.filter (\( fieldError, _ ) -> fieldError == field)


formatErrors : List Error -> Html msg
formatErrors errors =
    errors
        |> List.map (\( _, error ) -> p [] [ text error ])
        |> div [ class "text-danger", class "custom-validation-message" ]


type Msg
    = GotConfig (Result Http.Error Config.Config)
    | SetField Application.Field String
    | SetYearOfStudy String
    | SetCostOfAttendance String
    | ReceiveAssessment String
    | SubmitApplication
    | ReceiveSubmissionResponse (Result (List String) Assessment.Assessment)
    | ResetApplication


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case msg of
        GotConfig result ->
            case result of
                Ok config ->
                    let
                        yos =
                            List.head config.yearsOfStudy

                        coa =
                            List.head config.costsOfAttendance
                    in
                    doAssessment
                        { model
                            | application =
                                model.application
                                    |> Application.setYearOfStudy
                                        (Maybe.withDefault model.application.yearOfStudy yos)
                                    |> Application.setCostOfAttendance
                                        (Maybe.withDefault model.application.costOfAttendance coa)
                            , config = config
                            , state = Editing
                        }
                        Application.YearOfStudy

                Err err ->
                    case err of
                        Http.BadBody errMsg ->
                            ( { model | errors = Just [ errMsg ] }, Cmd.none )

                        _ ->
                            ( { model | errors = Just [ "Couldn't load config." ] }, Cmd.none )

        SetField field value ->
            if model.applicationWasValidated then
                let
                    app =
                        Application.setField model.application field value
                in
                case Application.validate app model.assessment of
                    Ok _ ->
                        doAssessment
                            { model
                                | applicationErrors = []
                                , application = app
                            }
                            field

                    Err errors ->
                        doAssessment
                            { model
                                | applicationErrors = errors
                                , application = app
                            }
                            field

            else
                doAssessment
                    { model
                        | application =
                            Application.setField model.application field value
                    }
                    field

        SetYearOfStudy id ->
            let
                yos =
                    model.config.yearsOfStudy
                        |> List.filter (\y -> y.id == id)
                        |> List.head
            in
            doAssessment
                { model
                    | application =
                        model.application
                            |> Application.setYearOfStudy
                                (Maybe.withDefault model.application.yearOfStudy yos)
                }
                Application.YearOfStudy

        SetCostOfAttendance id ->
            let
                coa =
                    model.config.costsOfAttendance
                        |> List.filter (\c -> c.id == id)
                        |> List.head
            in
            doAssessment
                { model
                    | application =
                        model.application
                            |> Application.setCostOfAttendance
                                (Maybe.withDefault model.application.costOfAttendance coa)
                }
                Application.CostOfAttendance

        ReceiveAssessment str ->
            case Assessment.decode str of
                Ok ass ->
                    ( { model | assessment = ass }, Cmd.none )

                Err e ->
                    ( { model
                        | errors =
                            Just [ Json.Decode.errorToString e ]
                      }
                    , Cmd.none
                    )

        SubmitApplication ->
            case Application.validate model.application model.assessment of
                Ok _ ->
                    ( { model
                        | applicationErrors = []
                        , applicationWasValidated = True
                        , state = Submitting
                      }
                    , Application.post model.application ReceiveSubmissionResponse
                    )

                Err errors ->
                    ( { model
                        | applicationErrors = errors
                        , applicationWasValidated = True
                        , errors = Just [ """Check
                        you have completed all required fields and try
                        again.""" ]
                      }
                    , Cmd.none
                    )

        ReceiveSubmissionResponse res ->
            case res of
                Ok _ ->
                    ( { model
                        | state = Submitted
                        , errors = Nothing
                      }
                    , Cmd.none
                    )

                Err errs ->
                    ( { model
                        | state = Editing
                        , errors = Just errs
                      }
                    , Cmd.none
                    )

        ResetApplication ->
            let
                yos =
                    List.head model.config.yearsOfStudy

                coa =
                    List.head model.config.costsOfAttendance
            in
            doAssessment
                { model
                    | errors = Nothing
                    , state = Editing
                    , applicationErrors = []
                    , applicationWasValidated = False
                    , application =
                        Application.default
                            |> Application.setYearOfStudy
                                (Maybe.withDefault model.application.yearOfStudy yos)
                            |> Application.setCostOfAttendance
                                (Maybe.withDefault model.application.costOfAttendance coa)
                }
                Application.YearOfStudy


doAssessment : Model -> Application.Field -> ( Model, Cmd msg )
doAssessment model field =
    ( model
    , if
        List.any (\f -> f == field)
            [ Application.FirstName
            , Application.Surname
            , Application.StudentNumber
            , Application.SocialSecurityNumber
            , Application.EmailAddress
            , Application.Course
            , Application.Assistance
            ]
      then
        Cmd.none

      else
        assessApplication <|
            Application.encodeWithConfig model.application
                model.config
    )


port assessApplication : String -> Cmd msg


port receiveAssessment : (String -> msg) -> Sub msg


subscriptions : Model -> Sub Msg
subscriptions _ =
    receiveAssessment ReceiveAssessment
